// supervisor-before-dispatch-gate-step.ts
//
// The §18 gate step of `runSupervisorUntilIdle` (quiet hours / session cap /
// free disk / pin drift / cost & attempt ceilings), split out of
// supervisor-run-until-idle.ts to keep that file under the 200-line budget
// (plan 20260924-0205). The caller maps the verdict back onto the loop:
// `terminal` => return "blocked", `retry` => return "before_dispatch_blocked"
// with the decision's code/reason/resumeAt (phase 06 — the loop no longer
// polls-and-waits forever on a transient block), `proceed` => dispatch.
//
// A NEW attempt is about to dispatch when this runs — the same tick-boundary
// point where the loop drains redirect/revert/abort, so gating here never
// interrupts an in-flight worker. A transient block skips dispatch THIS tick
// only; the control loop (transport/halt-directive checks) keeps running
// exactly as it does through a quota sleep.
import { randomUUID } from "node:crypto";
import type { BeforeDispatchDecision } from "./supervisor-dispatch-guards.js";
import { evaluateBeforeDispatch } from "./supervisor-dispatch-guards.js";
import type { RunUntilIdleInput, RunUntilIdleResult } from "./supervisor-run-until-idle-types.js";

export type BeforeDispatchGateVerdict = "proceed" | "retry" | "terminal";

/**
 * Cost/attempt ceilings are PERMANENT once breached — terminal for the
 * mission on the startup path exactly as mid-run (re-review 2026-09-26,
 * A-F1: startup used to map them onto retryable `before_dispatch_blocked`).
 */
export function isTerminalBeforeDispatchCode(code: string | undefined): boolean {
  return code === "cost_limit_reached" || code === "attempt_limit_reached";
}

export interface BeforeDispatchGateResult {
  verdict: BeforeDispatchGateVerdict;
  decision?: BeforeDispatchDecision;
}

export type BeforeDispatchGateInput = Pick<
  RunUntilIdleInput,
  "beforeDispatch" | "missionWorkspace" | "controlRoot" | "now" | "store" | "progress"
>;

/**
 * `blocked:<code>` status label — the same commit shape the startup path
 * writes (journalStartupBeforeDispatchBlocked), so `status --json` reports
 * why regardless of whether the block was observed at startup or mid-run.
 * Commits through the caller's own open store (not a second
 * MissionStore.open — that would collide with the journal lease the running
 * `run` already holds).
 */
async function journalBlockedStatusLabel(
  store: BeforeDispatchGateInput["store"],
  code: string | undefined,
): Promise<void> {
  await store.commit([
    {
      event_id: randomUUID(),
      delta: { kind: "mission_status_changed", status: `blocked:${code ?? "before_dispatch"}` },
    },
  ]);
}

export async function checkBeforeDispatchGate(
  input: BeforeDispatchGateInput,
): Promise<BeforeDispatchGateResult> {
  // No §18 deps supplied => no ops gating this run.
  if (!input.beforeDispatch) return { verdict: "proceed" };
  const decision = await evaluateBeforeDispatch({
    missionWorkspace: input.beforeDispatch.missionWorkspace ?? input.missionWorkspace,
    controlRoot: input.controlRoot,
    config: input.beforeDispatch.config,
    now: (input.now ?? (() => new Date()))(),
    attemptTimestampsIso: input.beforeDispatch.attemptTimestampsIso
      ? await input.beforeDispatch.attemptTimestampsIso()
      : [],
    probeClaudeVersion: input.beforeDispatch.probeClaudeVersion,
    statfs: input.beforeDispatch.statfs,
    // P13-review must-fix #4: cost/attempt ceilings are PERMANENT once
    // breached (unlike quiet_hours/session-cap/disk, which are transient and
    // worth another tick's retry) — passing `store` here makes
    // evaluateBeforeDispatch's own checkCostAttemptCeilingAndMaybeEscalate
    // commit the WAITING_HUMAN receipt (same mechanism the loop's
    // escalate_waiting_human branch relies on), and the `terminal` verdict
    // gives the loop a real exit instead of spinning up to maxTicks. Every
    // OTHER blocked code (quiet_hours, max_sessions_per_hour, min_free_disk,
    // pin_drift) keeps the retry behaviour — only a cost/attempt ceiling
    // breach is terminal for this mission.
    store: input.store,
    progress: input.progress,
  });
  if (decision.blocked && isTerminalBeforeDispatchCode(decision.code)) {
    // Re-review backlog 2026-09-26: the ceiling guard has already committed
    // its WAITING_HUMAN receipt through `store` (evaluateBeforeDispatch); the
    // label goes on top exactly as the startup path does, so the mission
    // reads `blocked:<code>` whether the ceiling tripped at tick 0 or mid-run
    // (mid-run used to stop at WAITING_HUMAN — the two paths had drifted).
    await journalBlockedStatusLabel(input.store, decision.code);
    return { verdict: "terminal", decision };
  }
  if (decision.blocked) {
    // Phase 06 [N3 vòng 1]: journal the same way the startup before-dispatch
    // path does (supervisor-mission-run.ts's gateBlocked commit).
    await journalBlockedStatusLabel(input.store, decision.code);
    return { verdict: "retry", decision };
  }
  return { verdict: "proceed", decision };
}

/**
 * NEW-1 fix (dogfood recheck 260925): shared verdict-to-outcome mapping, used
 * by BOTH the feature-ticket dispatch gate AND the candidate-review dispatch
 * gate in supervisor-run-until-idle.ts — a review session (T-9001/T-9002) is
 * a real worker dispatch, so it must map a `terminal`/`retry` verdict onto
 * the exact same `RunUntilIdleResult` shape a blocked feature ticket gets
 * (same outcome, code, reason, resumeAt). Returns `undefined` for `proceed`
 * so the caller knows to continue with its own next step.
 */
export function mapBeforeDispatchGateVerdictToResult(
  gate: BeforeDispatchGateResult,
  ticksRun: number,
  g2UnenforcedConditions: readonly string[] | undefined,
): RunUntilIdleResult | undefined {
  if (gate.verdict === "terminal") {
    // Code + reason ride along so printRunOutcome can name the ceiling and
    // the human action instead of a bare `outcome=blocked`.
    return {
      outcome: "blocked",
      ticksRun,
      beforeDispatchCode: gate.decision?.code,
      beforeDispatchReason: gate.decision?.reason,
      g2UnenforcedConditions,
    };
  }
  if (gate.verdict === "retry") {
    return {
      outcome: "before_dispatch_blocked",
      ticksRun,
      beforeDispatchCode: gate.decision?.code,
      beforeDispatchReason: gate.decision?.reason,
      resumeAt: gate.decision?.resumeAt,
      g2UnenforcedConditions,
    };
  }
  return undefined;
}
